import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Target, FileText, Database, Cpu } from 'lucide-react';
import { targets } from '../data/newContent';

const TargetsSection = () => {
  return (
    <section id="targets" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-5">
        {Array.from({ length: 30 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-20 bg-gradient-to-b from-green-400/30 to-transparent"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              opacity: [0, 0.5, 0],
              y: [0, -50]
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 3
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Targets & <span className="text-green-400">Validation</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            We publish targets—objectives pending validation—along with methods and acceptance thresholds so progress is transparent and reproducible.
          </p>
          
          <div className="inline-flex items-center gap-2 bg-yellow-900/20 border border-yellow-400/30 rounded-lg px-6 py-3">
            <FileText className="text-yellow-400" size={20} />
            <span className="text-yellow-300 text-sm">
              These are goals, not claims. Validation in progress with public methodologies.
            </span>
          </div>
        </motion.div>

        {/* Targets Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="space-y-6"
        >
          {targets.map((target, index) => {
            const icons = [Target, Database, Cpu];
            const IconComponent = icons[index % icons.length];
            
            return (
              <motion.div
                key={target.domain}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="bg-slate-800/50 border-green-400/20 hover:border-green-400/40 transition-all">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-green-400/20 flex items-center justify-center">
                          <IconComponent className="text-green-400" size={24} />
                        </div>
                        <div>
                          <CardTitle className="text-white text-xl">{target.domain}</CardTitle>
                          <CardDescription className="text-gray-400">
                            Target: {target.target_text}
                          </CardDescription>
                        </div>
                      </div>
                      {target.status === 'Validated' ? (
                        <Badge variant="outline" className="border-green-400/60 bg-green-400/15 text-green-300">
                          Validated
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-green-400/30 text-green-400">
                          Pending Validation
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-green-400 mb-2">Acceptance Threshold</h4>
                          <p className="text-gray-300 text-sm">{target.tolerance_text}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-cyan-400 mb-2">Datasets</h4>
                          <p className="text-gray-300 text-sm">{target.datasets}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-purple-400 mb-2">Hardware</h4>
                          <p className="text-gray-300 text-sm">{target.hardware}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-blue-400 mb-2">Baseline Techniques</h4>
                          <p className="text-gray-300 text-sm">{target.baselines}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-orange-400 mb-2">Method Outline</h4>
                          <p className="text-gray-300 text-sm">{target.method_outline}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-pink-400 mb-2">Acceptance Criteria</h4>
                          <p className="text-gray-300 text-sm">{target.acceptance}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-700">
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <FileText size={14} />
                        <span>Reproducibility scripts and containers will be published upon validation completion</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Validation Process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-cyan-400/20">
            <CardHeader>
              <CardTitle className="text-2xl text-white text-center">Our Validation Commitment</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-cyan-400 font-bold text-lg mb-2">Transparent Methods</div>
                  <p className="text-gray-300 text-sm">
                    Every target includes detailed methodology, datasets, and hardware specifications
                  </p>
                </div>
                <div>
                  <div className="text-green-400 font-bold text-lg mb-2">Public Reproducibility</div>
                  <p className="text-gray-300 text-sm">
                    Scripts and containers will be published for third-party validation
                  </p>
                </div>
                <div>
                  <div className="text-purple-400 font-bold text-lg mb-2">Clear Thresholds</div>
                  <p className="text-gray-300 text-sm">
                    Acceptance criteria defined before results are claimed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default TargetsSection;
